import { parseJwt } from "./commonFunctions";
import IUser from "../types/services/IUser";
import ICharity from "../types/services/ICharity";
import ILoginResponse from "../types/services/ILoginResponse";
import { isOnboardingOperatorClaim } from "./onboarding/onboardingAccess";

export interface AdminSessionFromLogin {
  user: IUser;
  charities: ICharity[];
  realms: string[];
}

/**
 * Turns a login token payload into Redux `loginUser` arguments and derived flags.
 */
export function buildAdminSessionFromLoginResponse(
  response: ILoginResponse,
  loginUser: (user: IUser, currentCharity: ICharity, charities: ICharity[]) => void,
  preferredCharityId?: string
): AdminSessionFromLogin {
  const decoded = parseJwt(response.token);
  const claims =
    decoded.claims != null && typeof decoded.claims === "object"
      ? decoded.claims
      : decoded;
  if (!Array.isArray(claims.__realms)) {
    throw new Error("Invalid login token: missing __realms.");
  }
  const charities: ICharity[] = [];
  claims.__realms.forEach((charity: string) => {
    if (charity !== "superadmin" && charity !== "donation-reader") {
      charities.push({
        id: charity,
        name: "",
        role: claims[charity],
      });
    }
  });
  let realms: string[] = claims.__realms;
  const user: IUser = response.user;
  user.isSuperadmin = realms.indexOf("superadmin") !== -1;
  user.isDonationReader = realms.indexOf("donation-reader") !== -1;
  user.isOnboardingOperator = isOnboardingOperatorClaim(
    claims as Record<string, unknown>
  );
  realms = realms.filter(
    r => r !== "superadmin" && r !== "donation-reader"
  );
  const targetRealm =
    preferredCharityId ||
    (typeof claims.targetRealm === "string" ? claims.targetRealm : "") ||
    realms[0];
  if (
    targetRealm &&
    !charities.some(charity => charity.id === targetRealm)
  ) {
    charities.unshift({
      id: targetRealm,
      name: "",
      role: claims[targetRealm],
    });
  }
  const preferredCharity =
    charities.find(charity => charity.id === targetRealm) || charities[0];
  const orderedCharities = preferredCharity
    ? [
        preferredCharity,
        ...charities.filter(charity => charity.id !== preferredCharity.id),
      ]
    : charities;
  loginUser(user, { ...preferredCharity }, orderedCharities);
  return { user, charities: orderedCharities, realms };
}
